<template>
  <v-dialog :value="value" persistent max-width="900px">
    <v-card v-if="item !== null" data-cy="mv-bulk-review-form">
      <v-card-title class="d-block">
        <span class="text-h5">Bulk Review Proposals</span>
      </v-card-title>
      <v-card-text>
        <v-row class="my-0">
          <nv-show cols="12" sm="3" header="Proposal">
            {{ item.rule_type | humanize }}
          </nv-show>
          <nv-show cols="12" sm="4" header="Tree Id">
            <tree-item
              v-if="item.tree_id"
              :tree-id="item.tree_id"
              :country-code="item.localization"
            />
          </nv-show>
          <nv-show v-if="countryCode" cols="12" sm="4" header="Market">
            <CountryBadge :country="resolveCountryCode(item.localization)" />
          </nv-show>
          <nv-show cols="12" sm="5" header="Status">
            {{ item.status }}
          </nv-show>
          <nv-show cols="12" sm="5" header="Market">
            <CountryBadge :country="resolveCountryCode(item.localization)" />
          </nv-show>
          <nv-show cols="12" sm="5" header="Created By">
            {{ item.created_by }}
          </nv-show>
        </v-row>
        <v-row class="my-0">
          <template v-if="item.status === 'approved' || item.status === 'rejected'">
            <nv-show cols="7" sm="5" header="Reviewed By">
              {{ item.reviewed_by }}
            </nv-show>
            <nv-show v-if="item.comments" cols="10" sm="5" header="Comment">
              {{ item.comments }}
            </nv-show>
          </template>
        </v-row>
        <v-divider />
        <v-row class="my-0">
          <template v-if="item.process_type === 'rule_import' && item.id">
            <bulk-reviews-table
              :id="item.id"
              :type="item.rule_type"
              :process-type="item.process_type"
              @selection-changed="handleSelectionChanged"
            />
            <template v-if="item.status === 'failed'">
              <bulk-error-message-table :error-messages="item.error_message" />
            </template>
          </template>
          <template v-else>
            <template v-if="item.status === 'failed' && item.error_message">
              <v-col cols="12">
                <bulk-error-message-table :error-messages="item.error_message" />
                <warning-bar message="Please re-upload a new CSV removing the above entries" />
              </v-col>
            </template>
            <template v-else-if="item.status && item.status !== 'failed'">
              <div>
                <bulk-reviews-table
                  :id="item.id"
                  :type="item.rule_type"
                  :process-type="item.process_type"
                  @selection-changed="handleSelectionChanged"
                />
              </div>
            </template>
          </template>
        </v-row>
        <v-form ref="form">
          <!-- <v-row v-if="status === 'pending' && $auth.hasPermissions('owl.review')" class="pt-6 my-0" align="center"
            no-gutters> -->
          <v-row
            v-if="item.status === 'pending' && $auth.hasPermissions('owl.review')"
            class="pt-6 my-0"
            align="center"
            no-gutters
          >
            <v-col cols="12">
              <span class="text-subtitle-1">Review:</span>
            </v-col>
            <v-col cols="auto" class="pr-6">
              <v-btn-toggle v-model="status" mandatory>
                <v-btn v-for="o in options" :key="o.value" :value="o.value" text small>
                  {{ o.text }}
                </v-btn>
              </v-btn-toggle>
            </v-col>
            <v-col>
              <v-text-field
                v-model="item.comments"
                :rules="item.status === 'rejected' ? rules.comment : []"
                validate-on-blur
                label="Comment"
              />
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>

      <v-card-actions>
        <nv-show cols="5" sm="5" header="Updated At">
          {{ item.updated_at | time }}
        </nv-show>
        <v-spacer></v-spacer>
        <v-btn color="error" @click="formCancel($refs.form)">Close</v-btn>
        <!-- <v-btn v-if="status === 'pending' && $auth.hasPermissions('owl.review')" :loading="loading" :disabled="loading" -->
        <v-btn
          v-if="item.status === 'pending' && $auth.hasPermissions('owl.review')"
          :loading="loading"
          :disabled="
            loading || (item.process_type === 'bulk_upload' ? selectedItems.length == 0 : false)
          "
          color="secondary"
          @click="formSubmit($refs.form)"
        >
          Submit
          <template #loader>
            <nv-circular-loader />
          </template>
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import HTTP from '@/api/ant';
import { FormMixin } from '@nielsen-media/ui-toolkit-vue-api';
import proposalMixin from '@/helpers/proposals';
import { resolveCountryCode } from '@/helpers/filters';
import TreeItem from '@/components/cls/TreeItem.vue';
import CountryBadge from '@/components/CountryBadge';
import BulkReviewsTable from '@/components/cls/BulkReviewsTable.vue';
import BulkErrorMessageTable from '@/components/cls/BulkErrorMessageTable.vue';
import WarningBar from '@/components/cls/WarningBar.vue';
import Rules from 'bsm-validation-rules';

export default {
  components: {
    CountryBadge,
    BulkReviewsTable,
    BulkErrorMessageTable,
    WarningBar,
    TreeItem,
  },
  mixins: [FormMixin, proposalMixin],
  props: {
    value: { type: Boolean, default: false },
    id: { type: Number, default: null },
  },
  data() {
    return {
      market: null,
      selectedItems: [],
      status: 'approve',
      options: [
        { text: 'Approve', value: 'approve' },
        { text: 'Reject', value: 'reject' },
      ],
      rules: {
        comment: [Rules.presence(), Rules.length({ max: 255 })],
      },
    };
  },
  methods: {
    async formInit() {
      if (this.id) {
        try {
          let { data } = await HTTP.get(`bulk_upload/process/${this.id}`);
          return data;
        } catch (err) {
          return {};
        }
      } else {
        return {};
      }
    },
    formSave() {
      if (this.item.process_type === 'bulk_upload') {
        const payload = {
          ids: this.selectedItems, // Already contains just the IDs
          bulk_upload_process_id: this.item.id,
          comments: this.item.comments,
        };
        return HTTP.put(`/${this.item.rule_type}/proposals/bulk/${this.status}`, payload, {
          niSkipFlash: true,
        })
          .then(() => {
            this.$flash(
              'success',
              `Proposals have been successfully ${
                this.status === 'approve' ? 'approved' : 'rejected'
              }`,
            );
          })
          .catch(err => {
            this.formErrorHandler(err);
          });
      } else {
        const payload = {
          action: this.status,
          bulk_upload_process_id: this.item.id,
          comments: this.item.comments,
        };
        return HTTP.post(`/rule_import/review`, payload, {
          niSkipFlash: true,
        })
          .then(() => {
            this.$flash(
              'success',
              `Proposals have been successfully ${
                this.status === 'approve' ? 'approved' : 'rejected'
              }`,
            );
          })
          .catch(err => {
            this.formErrorHandler(err);
          });
      }
    },
    handleSelectionChanged(selectedItems) {
      this.selectedItems = selectedItems.map(item => item.id); // Store only the IDs in parent component data
    },
    resolveCountryCode,
  },
};
</script>
<!-- <style scoped>
.scrollable-container {
  min-height: 200px;
  max-height: 400px;
  overflow-y: auto;
  padding: 0;
  background: #384557;
  width: 100%;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  color: #fff;
}
</style> -->
